import React, { useState, useEffect, useCallback } from 'react';
import './PendingAccountUpdates.css';
import SvgIcon from '../SvgIcon';

const PendingAccountUpdates = ({ adminUser, user, setToast }) => {
  const currentAdmin = adminUser || user;
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Modal States
  const [viewingRequest, setViewingRequest] = useState(null);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [rejectingRequest, setRejectingRequest] = useState(null);

  // Form states inside modals
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/api/admin/account-updates/pending?tab=${activeTab}&search=${encodeURIComponent(searchTerm)}`;
      const res = await fetch(url, {
        headers: {
          'x-admin-role': currentAdmin?.role || 'Super Admin'
        }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setRequests(data.data || []);
        if (data.counts) setCounts(data.counts);
      } else {
        if (setToast) setToast({ type: 'error', msg: data.message || 'Failed to fetch account updates.' });
      }
    } catch (err) {
      console.error('Fetch account updates error:', err);
      if (setToast) setToast({ type: 'error', msg: 'Failed to connect to the server.' });
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, currentAdmin, setToast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleOpenApproveModal = (req) => {
    setApprovingRequest(req);
    setApproveNotes('Approved by administrator.');
  };

  const handleOpenRejectModal = (req) => {
    setRejectingRequest(req);
    setRejectReason('');
  };

  const handleConfirmApprove = async () => {
    if (!approvingRequest) return;
    setProcessingId(approvingRequest.RequestID);

    try {
      const res = await fetch(`/api/admin/account-updates/${approvingRequest.RequestID}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-role': currentAdmin?.role || 'Super Admin'
        },
        body: JSON.stringify({
          adminId: currentAdmin?.id || currentAdmin?.adminId || null,
          adminName: currentAdmin?.fullName || currentAdmin?.name || 'Administrator',
          adminRole: currentAdmin?.role || 'Super Admin',
          reviewNotes: approveNotes.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        if (setToast) setToast({ type: 'success', msg: data.message || 'Account update approved successfully.' });
        setApprovingRequest(null);
        if (viewingRequest?.RequestID === approvingRequest.RequestID) setViewingRequest(null);
        fetchRequests();
      } else {
        if (setToast) setToast({ type: 'error', msg: data.message || 'Failed to approve update.' });
      }
    } catch (err) {
      console.error(err);
      if (setToast) setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectingRequest) return;
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejecting this request.');
      return;
    }

    setProcessingId(rejectingRequest.RequestID);

    try {
      const res = await fetch(`/api/admin/account-updates/${rejectingRequest.RequestID}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-role': currentAdmin?.role || 'Super Admin'
        },
        body: JSON.stringify({
          adminId: currentAdmin?.id || currentAdmin?.adminId || null,
          adminName: currentAdmin?.fullName || currentAdmin?.name || 'Administrator',
          adminRole: currentAdmin?.role || 'Super Admin',
          rejectionReason: rejectReason.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        if (setToast) setToast({ type: 'success', msg: data.message || 'Account update request rejected.' });
        setRejectingRequest(null);
        if (viewingRequest?.RequestID === rejectingRequest.RequestID) setViewingRequest(null);
        fetchRequests();
      } else {
        if (setToast) setToast({ type: 'error', msg: data.message || 'Failed to reject update.' });
      }
    } catch (err) {
      console.error(err);
      if (setToast) setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setProcessingId(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day}/${month}/${year} ${time}`;
  };

  return (
    <div className="account-updates-workspace" data-testid="pending-account-updates-workspace">
      <div className="account-updates-card">
        {/* Header */}
        <div className="account-updates-header">
          <div className="account-updates-title">
            <h3>Pending Account Updates</h3>
            <p>Review, verify, and approve or reject learner requests to update primary emails and sector categories.</p>
          </div>
          <div className="account-updates-actions">
            <div className="account-updates-search-box">
              <SvgIcon name="search" size={16} />
              <input
                type="text"
                placeholder="Search learner, email, organisation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="account-updates-search-input"
              />
            </div>
            <button
              className="account-updates-refresh-btn"
              onClick={fetchRequests}
              title="Refresh requests"
              data-testid="refresh-account-updates-btn"
            >
              <SvgIcon name="refresh" size={14} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="account-updates-tabs-bar">
          <button
            className={`account-updates-tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending-updates"
          >
            <span>Pending Approvals</span>
            <span className="account-updates-tab-badge" data-testid="pending-count-badge">
              {counts.pending}
            </span>
          </button>
          <button
            className={`account-updates-tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
            data-testid="tab-approved-updates"
          >
            <span>Approved History</span>
            <span className="account-updates-tab-badge">{counts.approved}</span>
          </button>
          <button
            className={`account-updates-tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => setActiveTab('rejected')}
            data-testid="tab-rejected-updates"
          >
            <span>Rejected History</span>
            <span className="account-updates-tab-badge">{counts.rejected}</span>
          </button>
          <button
            className={`account-updates-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
            data-testid="tab-all-updates"
          >
            <span>All Requests</span>
            <span className="account-updates-tab-badge">{counts.total}</span>
          </button>
        </div>

        {/* Table Body */}
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
            Loading account update requests...
          </div>
        ) : requests.length === 0 ? (
          <div style={{ padding: '3.5rem 2rem', textAlign: 'center', color: '#64748b' }} data-testid="empty-requests-msg">
            {activeTab === 'pending'
              ? '🎉 No pending account updates require review.'
              : 'No account update records found.'}
          </div>
        ) : (
          <div className="account-updates-table-wrapper">
            <table className="account-updates-table" data-testid="account-updates-table">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Primary Email</th>
                  <th>Sector & Organisation</th>
                  <th>Submitted At</th>
                  <th>Review Status</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const hasEmailChange = Boolean(r.RequestedEmail);
                  const hasSectorChange = Boolean(r.RequestedSector);
                  const hasOrgChange = Boolean(r.RequestedOrganisationName && r.RequestedOrganisationName !== r.CurrentOrganisationName);

                  return (
                    <tr key={r.RequestID} data-testid={`request-row-${r.RequestID}`}>
                      {/* Learner Info */}
                      <td>
                        <div className="learner-cell-name">
                          <span>{r.FullName}</span>
                        </div>
                        <div className="learner-cell-sub">
                          <span>Username: @{r.Username}</span> • <span style={{ color: '#64748b' }}>ID: #{r.LearnerID}</span>
                        </div>
                      </td>

                      {/* Primary Email */}
                      <td>
                        <div className="email-cell">
                          <span className="email-curr">{r.CurrentEmail}</span>
                          {hasEmailChange ? (
                            <span className="email-req" data-testid={`req-email-${r.RequestID}`}>
                              <span className="change-arrow">➔</span> {r.RequestedEmail}
                            </span>
                          ) : (
                            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>No email change</span>
                          )}
                        </div>
                      </td>

                      {/* Sector & Organisation */}
                      <td>
                        <div className="sector-cell">
                          <span className="sector-curr">
                            {r.CurrentSector} {r.CurrentOrganisationName ? `(${r.CurrentOrganisationName})` : ''}
                          </span>
                          {(hasSectorChange || hasOrgChange) ? (
                            <span className="sector-req" data-testid={`req-sector-${r.RequestID}`}>
                              <span className="change-arrow">➔</span> {r.RequestedSector || r.CurrentSector}
                              {r.RequestedOrganisationName ? ` (${r.RequestedOrganisationName})` : ''}
                            </span>
                          ) : (
                            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>No sector change</span>
                          )}
                        </div>
                      </td>

                      {/* Submitted At */}
                      <td>
                        <div style={{ fontSize: '0.82rem', color: '#334155' }}>
                          {formatDateTime(r.SubmittedAt)}
                        </div>
                      </td>

                      {/* Review Status & SLA Indicator */}
                      <td>
                        <div>
                          <span className={`status-pill ${r.Status.toLowerCase()}`} data-testid={`status-pill-${r.RequestID}`}>
                            {r.Status === 'Pending' ? '⏳ Pending' : r.Status === 'Approved' ? '✓ Approved' : '✕ Rejected'}
                          </span>
                        </div>
                        {r.Status === 'Pending' && r.slaStatus && (
                          <div>
                            <span className={`sla-pill ${r.slaStatus}`} data-testid={`sla-badge-${r.RequestID}`}>
                              {r.slaIsOverdue ? '⚠️ ' : ''}{r.slaBadgeText}
                            </span>
                          </div>
                        )}
                        {r.Status !== 'Pending' && r.ReviewedAt && (
                          <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '3px' }}>
                            {formatDateTime(r.ReviewedAt)}
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="action-btns-group">
                          <button
                            className="btn-action-view"
                            onClick={() => setViewingRequest(r)}
                            title="View Details"
                            data-testid={`btn-view-${r.RequestID}`}
                          >
                            Details
                          </button>

                          {r.Status === 'Pending' && (
                            <>
                              <button
                                className="btn-action-approve"
                                onClick={() => handleOpenApproveModal(r)}
                                disabled={processingId === r.RequestID}
                                title="Approve Changes"
                                data-testid={`btn-approve-${r.RequestID}`}
                              >
                                Approve
                              </button>
                              <button
                                className="btn-action-reject"
                                onClick={() => handleOpenRejectModal(r)}
                                disabled={processingId === r.RequestID}
                                title="Reject Changes"
                                data-testid={`btn-reject-${r.RequestID}`}
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── View Details Modal ── */}
      {viewingRequest && (
        <div className="account-update-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setViewingRequest(null); }}>
          <div className="account-update-modal-dialog" data-testid="details-modal">
            <div className="account-update-modal-header">
              <h4>Request Details #{viewingRequest.RequestID}</h4>
              <button
                className="dismiss-banner-btn"
                onClick={() => setViewingRequest(null)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="account-update-modal-body">
              {/* Learner Info */}
              <div className="details-summary-grid">
                <div className="details-summary-item">
                  <span>Learner Name</span>
                  <span>{viewingRequest.FullName}</span>
                </div>
                <div className="details-summary-item">
                  <span>Username</span>
                  <span>@{viewingRequest.Username}</span>
                </div>
                <div className="details-summary-item">
                  <span>Learner ID</span>
                  <span>#{viewingRequest.LearnerID}</span>
                </div>
                <div className="details-summary-item">
                  <span>Current Request Status</span>
                  <span style={{ color: viewingRequest.Status === 'Approved' ? '#059669' : viewingRequest.Status === 'Rejected' ? '#dc2626' : '#2563eb' }}>
                    {viewingRequest.Status}
                  </span>
                </div>
              </div>

              {/* Side-by-Side Comparison */}
              <div className="comparison-grid">
                <div className="comparison-box current">
                  <h5>Current Values</h5>
                  <div className="comparison-row">
                    <span className="comp-label">Primary Email</span>
                    <span className="comp-val">{viewingRequest.CurrentEmail}</span>
                  </div>
                  <div className="comparison-row">
                    <span className="comp-label">Sector Category</span>
                    <span className="comp-val">{viewingRequest.CurrentSector}</span>
                  </div>
                  <div className="comparison-row">
                    <span className="comp-label">Organisation</span>
                    <span className="comp-val">{viewingRequest.CurrentOrganisationName || 'N/A'}</span>
                  </div>
                </div>

                <div className="comparison-box requested">
                  <h5>Requested Values</h5>
                  <div className="comparison-row">
                    <span className="comp-label">Requested Primary Email</span>
                    <span className={`comp-val ${viewingRequest.RequestedEmail ? 'changed' : ''}`}>
                      {viewingRequest.RequestedEmail || `${viewingRequest.CurrentEmail} (Unchanged)`}
                    </span>
                  </div>
                  <div className="comparison-row">
                    <span className="comp-label">Requested Sector Category</span>
                    <span className={`comp-val ${viewingRequest.RequestedSector ? 'changed' : ''}`}>
                      {viewingRequest.RequestedSector || `${viewingRequest.CurrentSector} (Unchanged)`}
                    </span>
                  </div>
                  <div className="comparison-row">
                    <span className="comp-label">Requested Organisation</span>
                    <span className={`comp-val ${viewingRequest.RequestedOrganisationName ? 'changed' : ''}`}>
                      {viewingRequest.RequestedOrganisationName || `${viewingRequest.CurrentOrganisationName || 'N/A'} (Unchanged)`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Timestamps & Review Info */}
              <div className="details-summary-grid">
                <div className="details-summary-item">
                  <span>Submission Timestamp</span>
                  <span>{formatDateTime(viewingRequest.SubmittedAt)}</span>
                </div>
                <div className="details-summary-item">
                  <span>SLA Turnaround Status</span>
                  <span>{viewingRequest.slaBadgeText || 'N/A'}</span>
                </div>
                {viewingRequest.ReviewedAt && (
                  <>
                    <div className="details-summary-item">
                      <span>Reviewed By</span>
                      <span>{viewingRequest.ReviewerName || `Admin #${viewingRequest.ReviewedBy}`}</span>
                    </div>
                    <div className="details-summary-item">
                      <span>Reviewed Timestamp</span>
                      <span>{formatDateTime(viewingRequest.ReviewedAt)}</span>
                    </div>
                  </>
                )}
              </div>

              {viewingRequest.RejectionReason && (
                <div className="rejection-reason-box" style={{ margin: 0 }}>
                  <strong>Rejection Reason:</strong> {viewingRequest.RejectionReason}
                </div>
              )}
              {viewingRequest.ReviewNotes && (
                <div style={{ background: '#f8fafc', padding: '10px 14px', borderRadius: '6px', fontSize: '0.85rem' }}>
                  <strong>Admin Notes:</strong> {viewingRequest.ReviewNotes}
                </div>
              )}
            </div>

            <div className="account-update-modal-footer">
              {viewingRequest.Status === 'Pending' && (
                <>
                  <button
                    className="btn-action-approve"
                    style={{ padding: '0.5rem 1rem' }}
                    onClick={() => {
                      const req = viewingRequest;
                      setViewingRequest(null);
                      handleOpenApproveModal(req);
                    }}
                  >
                    Approve Request
                  </button>
                  <button
                    className="btn-action-reject"
                    style={{ padding: '0.5rem 1rem' }}
                    onClick={() => {
                      const req = viewingRequest;
                      setViewingRequest(null);
                      handleOpenRejectModal(req);
                    }}
                  >
                    Reject Request
                  </button>
                </>
              )}
              <button
                className="btn-action-view"
                style={{ padding: '0.5rem 1rem' }}
                onClick={() => setViewingRequest(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve Modal ── */}
      {approvingRequest && (
        <div className="account-update-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setApprovingRequest(null); }}>
          <div className="account-update-modal-dialog" data-testid="approve-modal">
            <div className="account-update-modal-header">
              <h4>Confirm Approval — Request #{approvingRequest.RequestID}</h4>
              <button className="dismiss-banner-btn" onClick={() => setApprovingRequest(null)}>✕</button>
            </div>
            <div className="account-update-modal-body">
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#334155', lineHeight: 1.5 }}>
                You are about to approve the requested account changes for <strong>{approvingRequest.FullName}</strong> ({approvingRequest.Username}).
              </p>

              {/* Side-by-side Summary of changes to apply */}
              <div className="comparison-grid">
                <div className="comparison-box">
                  <h5>Current Values</h5>
                  <div className="comparison-row">
                    <span className="comp-label">Email:</span>
                    <span className="comp-val">{approvingRequest.CurrentEmail}</span>
                  </div>
                  <div className="comparison-row">
                    <span className="comp-label">Sector:</span>
                    <span className="comp-val">{approvingRequest.CurrentSector}</span>
                  </div>
                </div>

                <div className="comparison-box requested">
                  <h5>New Approved Values</h5>
                  <div className="comparison-row">
                    <span className="comp-label">Email:</span>
                    <span className="comp-val changed">
                      {approvingRequest.RequestedEmail || `${approvingRequest.CurrentEmail} (Unchanged)`}
                    </span>
                  </div>
                  <div className="comparison-row">
                    <span className="comp-label">Sector:</span>
                    <span className="comp-val changed">
                      {approvingRequest.RequestedSector || `${approvingRequest.CurrentSector} (Unchanged)`}
                      {approvingRequest.RequestedOrganisationName ? ` (${approvingRequest.RequestedOrganisationName})` : ''}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: '6px', fontSize: '0.82rem', color: '#166534' }}>
                ✓ The learner's active login email and profile will be updated immediately. Dependent billing, course subsidies, and access rules will be recalculated automatically in a secure transaction.
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                  Admin Review Notes (Optional):
                </label>
                <input
                  type="text"
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  placeholder="e.g., Eligibility verified with HR department"
                  data-testid="approve-notes-input"
                  style={{
                    padding: '0.65rem 0.85rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '0.85rem'
                  }}
                />
              </div>
            </div>

            <div className="account-update-modal-footer">
              <button
                className="btn-action-approve"
                style={{ padding: '0.6rem 1.25rem' }}
                onClick={handleConfirmApprove}
                disabled={processingId === approvingRequest.RequestID}
                data-testid="confirm-approve-btn"
              >
                {processingId === approvingRequest.RequestID ? 'Applying Changes...' : 'Confirm & Apply Approval'}
              </button>
              <button
                className="btn-action-view"
                style={{ padding: '0.6rem 1.25rem' }}
                onClick={() => setApprovingRequest(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {rejectingRequest && (
        <div className="account-update-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setRejectingRequest(null); }}>
          <div className="account-update-modal-dialog" data-testid="reject-modal">
            <div className="account-update-modal-header">
              <h4>Reject Account Update Request #{rejectingRequest.RequestID}</h4>
              <button className="dismiss-banner-btn" onClick={() => setRejectingRequest(null)}>✕</button>
            </div>
            <div className="account-update-modal-body">
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#334155', lineHeight: 1.5 }}>
                You are rejecting the account update request for <strong>{rejectingRequest.FullName}</strong> ({rejectingRequest.Username}).
              </p>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: '6px', fontSize: '0.82rem', color: '#475569' }}>
                ℹ The learner's current email (<strong>{rejectingRequest.CurrentEmail}</strong>) and sector (<strong>{rejectingRequest.CurrentSector}</strong>) will remain completely active and unchanged.
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                  Reason for Rejection <span style={{ color: '#dc2626' }}>*</span>:
                </label>
                <textarea
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide an explanation for the learner (e.g. Official domain verification failed, company email requires HR verification)..."
                  data-testid="reject-reason-input"
                  style={{
                    padding: '0.65rem 0.85rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit'
                  }}
                  required
                />
              </div>
            </div>

            <div className="account-update-modal-footer">
              <button
                className="btn-action-reject"
                style={{ padding: '0.6rem 1.25rem', backgroundColor: '#dc2626', color: '#ffffff' }}
                onClick={handleConfirmReject}
                disabled={processingId === rejectingRequest.RequestID || !rejectReason.trim()}
                data-testid="confirm-reject-btn"
              >
                {processingId === rejectingRequest.RequestID ? 'Processing...' : 'Confirm Rejection'}
              </button>
              <button
                className="btn-action-view"
                style={{ padding: '0.6rem 1.25rem' }}
                onClick={() => setRejectingRequest(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingAccountUpdates;
